type Position =
  | "in-place"
  | "top-start"
  | "top"
  | "top-end"
  | "left-start"
  | "left"
  | "left-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end"
  | "right-start"
  | "right"
  | "right-end";

type PositionStyle = {
  transform: string;
};

/**
 * Calculate the position style for a portal element based on child bounds
 *
 * @param childBounds - The bounding rectangle of the child element
 * @param position - The desired position relative to the child element
 * @param relative - Whether to use relative positioning (without scroll offset)
 * @default false
 * @returns Style object with transform property
 */
export const getPosition = (
  childBounds: DOMRect,
  position: Position,
  relative = false,
): PositionStyle => {
  switch (position) {
    case "in-place": {
      const x =
        childBounds.left +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return { transform: `translate(${x}px, ${y}px)` };
    }
    case "top-start": {
      const x =
        childBounds.left +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return { transform: `translate(${x}px, calc(${y}px - 100% - 0.5rem))` };
    }
    case "top": {
      const x =
        childBounds.left +
        childBounds.width / 2 +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px - 50%), calc(${y}px - 100% - 0.5rem))`,
      };
    }
    case "top-end": {
      const x =
        childBounds.left +
        childBounds.width +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px - 100%), calc(${y}px - 100% - 0.5rem))`,
      };
    }
    case "left-start": {
      const x =
        childBounds.left +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return { transform: `translate(calc(${x}px - 100% - 0.5rem), ${y}px)` };
    }
    case "left": {
      const x =
        childBounds.left +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        childBounds.height / 2 +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px - 100% - 0.5rem), calc(${y}px - 50%))`,
      };
    }
    case "left-end": {
      const x =
        childBounds.left +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        childBounds.height +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px - 100% - 0.5rem), calc(${y}px - 100%))`,
      };
    }
    case "bottom-start": {
      const x =
        childBounds.left +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        childBounds.height +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return { transform: `translate(${x}px, calc(${y}px + 0.5rem))` };
    }
    case "bottom": {
      const x =
        childBounds.left +
        childBounds.width / 2 +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        childBounds.height +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px - 50%), calc(${y}px + 0.5rem))`,
      };
    }
    case "bottom-end": {
      const x =
        childBounds.left +
        childBounds.width +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        childBounds.height +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px - 100%), calc(${y}px + 0.5rem))`,
      };
    }
    case "right-start": {
      const x =
        childBounds.left +
        childBounds.width +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return { transform: `translate(calc(${x}px + 0.5rem), ${y}px)` };
    }
    case "right": {
      const x =
        childBounds.left +
        childBounds.width +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        childBounds.height / 2 +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px + 0.5rem), calc(${y}px - 50%))`,
      };
    }
    case "right-end": {
      const x =
        childBounds.left +
        childBounds.width +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        childBounds.height +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px + 0.5rem), calc(${y}px - 100%))`,
      };
    }
    default: {
      const x =
        childBounds.left +
        childBounds.width / 2 +
        (relative ? 0 : window.document.documentElement.scrollLeft);
      const y =
        childBounds.top +
        (relative ? 0 : window.document.documentElement.scrollTop);

      return {
        transform: `translate(calc(${x}px - 50%), calc(${y}px - 100% - 0.5rem))`,
      };
    }
  }
};
